import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Renderer2,
    ViewChild
} from '@angular/core';
import * as _ from 'lodash-es';
import {fromEvent, merge, Observable, Subject, Subscription} from 'rxjs';
import {EventListenerOptions} from 'rxjs/internal/observable/fromEvent';
import {animationFrameScheduler} from 'rxjs/internal/scheduler/animationFrame';
import {debounceTime, takeUntil, tap, throttleTime} from 'rxjs/operators';
import {TypedSimpleChanges} from '../../common/typed-on-changes';
import {destroy$} from '../destroyable';
import {SCROLLING_BAR, ScrollingBarComponent} from '../scrolling-bar/scrolling-bar.component';
import {CustomScrollbarService} from './custom-scrollbar.service';
import {Scrollbar, SCROLLBAR, ScrollingAxis} from './scrollbar';

export enum Ending {
    START,
    END
}

type Point = [number, number];

export interface CustomScrollbarScrollEvent {
    endOfMove: boolean;
    isUserScrolling: boolean;
}

export interface CustomScrollbarScrollOffsetEvent {
    axis: ScrollingAxis;
    scrollOffset: number;
    barLengthStr: any;
}

interface BarMovingInfo {
    startPointerClientPos: Point;
    pos: Point;
    listeners: Array<() => boolean | void>;
    axis: ScrollingAxis;
    scrollingBar: ScrollingBarComponent;
    startScrollPos: number;
    ratio: number;
}

const DOCUMENT_SELECTOR = 'document';
const MIN_SCROLL_LENGTH = 10; // in percentage
const INACTIVE_SCROLL_CLASS = '-inactive';
const DISABLED_SCROLL_CLASS = '-disabled';
const DRAG_CLASS = '-drag';
const SCROLLING_CLASS = '-scrolling';

const DEFAULT_SCROLL_ENABLED_STATE = true;

const HIDDEN_NATIVE_SCROLLBAR_CLASS = '-hidden-native-scrollbar';

/**
 * Options for event listener, means that the handler won't cancel the event.
 * Should improve performance of browser's events system.
 * Ref: https://developers.google.com/web/updates/2016/06/passive-event-listeners
 */
const PASSIVE_EVENT_LISTENING: EventListenerOptions = {passive: true};

@Component({
    selector: '[customScrollbar]',
    templateUrl: 'custom-scrollbar.component.html',
    styleUrls: ['custom-scrollbar.component.scss'],
    providers: [
        {
            provide: SCROLLBAR,
            useExisting: forwardRef(() => CustomScrollbarComponent)
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomScrollbarComponent implements AfterViewInit, OnChanges, OnDestroy, Scrollbar {

    @Input('updateOn')
    public externalUpdate$: Observable<any>;

    @Input('customScrollbarEnabled')
    public set isEnabled(isEnabled: boolean) {
        this.setEnabledState(isEnabled);
    }
    public get isEnabled(): boolean {
        return this._isEnabled;
    }

    @Input()
    public scrollingBarHorizontal: ScrollingBarComponent;

    @Input()
    public scrollingBarVertical: ScrollingBarComponent;

    @Output('scroll')
    public onScrollEvent = new EventEmitter<CustomScrollbarScrollEvent>();

    @Output('scrollOffset')
    public onScrollOffset = new EventEmitter<CustomScrollbarScrollOffsetEvent>();

    @ViewChild('wrapper', {static: true})
    public wrapperElement: ElementRef;

    @ViewChild('container', {static: true})
    public containerElement: ElementRef;

    @ViewChild('innerScrollingBarHorizontal', {read: SCROLLING_BAR, static: false})
    public innerScrollingBarHorizontal: ScrollingBarComponent;

    @ViewChild('innerScrollingBarVertical', {read: SCROLLING_BAR, static: false})
    public innerScrollingBarVertical: ScrollingBarComponent;

    @ViewChild('calculateScrollWidthHelper', {static: true})
    public calculateScrollWidthHelperElement: ElementRef;

    public readonly scrollingStyle$ = new Subject<void>();

    public readonly destroy$ = destroy$();

    private isInactive = false;

    private movingInfo: BarMovingInfo;
    private readonly updateSubject$ = new Subject<void>();
    private update$: Observable<any>;
    private updateSubscription: Subscription;
    private isUserNativeScrolling: boolean = false;
    private isViewInitialized: boolean = false;

    private _isEnabled: boolean = DEFAULT_SCROLL_ENABLED_STATE;

    constructor(
        protected renderer: Renderer2,
        protected elementRef: ElementRef,
        protected customScrollbarService: CustomScrollbarService
    ) {}

    @HostListener('window:resize')
    public updateLayout(): void {
        this.updateSubject$.next();
    }

    public ngAfterViewInit(): void {
        this.isViewInitialized = true;

        this.initElements();
        this.listenToDragStart();
        this.listenToScrollingStyle();
        this.listenToNativeScrolling();

        this.setEnabledState(this.isEnabled, this.isEnabled !== DEFAULT_SCROLL_ENABLED_STATE);
    }

    public ngOnChanges(changes: TypedSimpleChanges<CustomScrollbarComponent>): void {
        if (changes.externalUpdate$) {
            if (this.updateSubscription) {
                this.updateSubscription.unsubscribe();
            }

            this.subscribeToUpdates();
        }
    }

    public ngOnDestroy(): void {
        this.stopMovingListeners();
    }

    /**
     * Scrolls the container to the passed child element.
     * Behaves similar to {@link scrollIntoView} with options {block: 'nearest'}.
     * The main goals are browser-compatibility and ability to define custom offsets.
     * @param {ElementRef} childElementRef
     */
    public scrollTo(childElementRef: ElementRef): void {
        this.scrollToInAxis(childElementRef, ScrollingAxis.Y);
        this.scrollToInAxis(childElementRef, ScrollingAxis.X);
    }

    public getScrollingPosOnAxis(axis: ScrollingAxis): number {
        const containerElement: HTMLElement = this.containerElement.nativeElement;

        return axis === ScrollingAxis.X
            ? containerElement.scrollLeft
            : containerElement.scrollTop;
    }

    public setScrollingPosOnAxis(axis: ScrollingAxis, posOnAxis: number): void {
        const containerElement: HTMLElement = this.containerElement.nativeElement;

        if (axis === ScrollingAxis.X) {
            containerElement.scrollLeft = posOnAxis;
        } else {
            containerElement.scrollTop = posOnAxis;
        }
    }

    protected scrollToInAxis(childElementRef: ElementRef, axis: ScrollingAxis): void {
        const childElement: HTMLElement = childElementRef.nativeElement;
        const wrapperElement: HTMLElement = this.wrapperElement.nativeElement;

        const initialScrollOffset = this.getScrollingPosOnAxis(axis);

        const initialElementOffset = axis === ScrollingAxis.Y
            ? childElement.offsetTop
            : childElement.offsetLeft;

        let scrollShift = 0;

        const view = {
            start: initialScrollOffset + this.getScrollToOffsetOnAxis(axis, Ending.START),
            end: initialScrollOffset + wrapperElement.clientHeight - this.getScrollToOffsetOnAxis(axis, Ending.END)
        };
        const elView = {
            start: initialElementOffset,
            end: initialElementOffset + childElement.clientHeight
        };

        if (elView.start > view.start && elView.end > view.end) {
            scrollShift = Math.min(elView.end - view.end, elView.start - view.start);
        } else if (elView.start < view.start && elView.end < view.end) {
            scrollShift = -Math.min(view.end - elView.end, view.start - elView.start);
        }

        if (scrollShift) {
            this.setScrollingPosOnAxis(axis, initialScrollOffset + scrollShift);
        }
    }

    protected getScrollToOffsetOnAxis(axis: ScrollingAxis, ending: Ending): number {
        return 0;
    }

    protected updateBars(): void {
        this.updateBar(ScrollingAxis.Y);
        this.updateBar(ScrollingAxis.X);
    }

    protected updateScrollEndingsVisibility(
        axis: ScrollingAxis,
        {isBothVisible, scrollLength, scrollOffset}: {
            isBothVisible?: boolean;
            scrollLength?: number;
            scrollOffset?: number;
        }
    ): void {
        // To be extended.
    }

    protected getScrollingBarByAxis(axis: ScrollingAxis): ScrollingBarComponent {
        return axis === ScrollingAxis.X
            ? this.scrollingBarHorizontal || this.innerScrollingBarHorizontal
            : this.scrollingBarVertical || this.innerScrollingBarVertical;
    }

    private subscribeToUpdates(): void {
        if (!this.isInactive) {
            this.update$ = this.updateSubject$.asObservable();

            if (this.externalUpdate$) {
                this.update$ = merge(this.update$, this.externalUpdate$);
            }

            this.updateSubscription = this.update$.pipe(
                takeUntil(this.destroy$)
            ).subscribe(() => this.updateBars());
        }
    }

    private initElements(): void {
        this.setScrollWidth();

        const listeners: Array<Observable<any>> = [];

        listeners.push(
            fromEvent(this.containerElement.nativeElement, 'scroll', PASSIVE_EVENT_LISTENING).pipe(
                throttleTime(0, animationFrameScheduler),
                tap(this.onScroll.bind(this))
            )
        );

        if (!this.isInactive) {
            listeners.push(
                fromEvent(this.containerElement.nativeElement, 'mouseenter', PASSIVE_EVENT_LISTENING).pipe(
                    throttleTime(0, animationFrameScheduler),
                    tap(this.updateBars.bind(this))
                )
            );

            if (!this.updateSubscription) {
                this.subscribeToUpdates();
            }

            this.updateContainer();
            this.updateBars();
        }

        merge(...listeners).pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private calcScrollbarWidth(): number | null {
        const calculateElement = this.calculateScrollWidthHelperElement.nativeElement;

        this.renderer.setStyle(calculateElement, 'display', 'block');
        const clientWidthWithoutBar = calculateElement.clientWidth;
        this.renderer.setStyle(calculateElement, 'overflow', 'scroll');

        const barWidth = clientWidthWithoutBar
            ? clientWidthWithoutBar - calculateElement.clientWidth
            : null;

        this.renderer.setStyle(calculateElement, 'display', 'none');

        return barWidth;
    }

    private setScrollWidth(): void {
        const calculateElement = this.calculateScrollWidthHelperElement.nativeElement;

        if (!this.customScrollbarService.scrollbarWidth && this.customScrollbarService.scrollbarWidth !== 0) {
            this.customScrollbarService.scrollbarWidth = this.calcScrollbarWidth();

            this.renderer.addClass(calculateElement, HIDDEN_NATIVE_SCROLLBAR_CLASS);
            this.customScrollbarService.hiddenScrollbarWidth = this.calcScrollbarWidth();
            this.renderer.removeClass(calculateElement, HIDDEN_NATIVE_SCROLLBAR_CLASS);
        }

        const hasExternalScrollingBars = this.scrollingBarVertical || this.scrollingBarHorizontal;

        if (this.customScrollbarService.scrollbarWidth === 0 && !hasExternalScrollingBars) {
            this.isInactive = true;
            this.renderer.addClass(this.wrapperElement.nativeElement, INACTIVE_SCROLL_CLASS);
        }
    }

    private updateContainer(): void {
        const containerNativeElement = this.containerElement.nativeElement;

        this.setStyles(containerNativeElement, {
            right: `-${this.customScrollbarService.hiddenScrollbarWidth}px`,
            bottom: `-${this.customScrollbarService.hiddenScrollbarWidth}px`,
            overflow: 'scroll'
        });
    }

    private updateBar(axis: ScrollingAxis): void {
        const scrollRatio = this.getScrollRatio(axis);

        if (scrollRatio >= 1) {
            this.updateScrollEndingsVisibility(axis, {isBothVisible: true});
            this.setBarVisibility(axis, false);
        } else {
            const containerElement: HTMLElement = this.containerElement.nativeElement;

            const barElement: HTMLElement = this.getScrollingBarByAxis(axis).barElementRef.nativeElement;

            const scrollLength = axis === ScrollingAxis.Y
                ? containerElement.scrollHeight
                : containerElement.scrollWidth;

            const scrollOffset = this.getScrollingPosOnAxis(axis);

            const barOffsetStr = (scrollOffset / scrollLength) * 100 + '%';
            const barLengthStr: string = Math.max(scrollRatio * 100, MIN_SCROLL_LENGTH) + '%';

            if (axis === ScrollingAxis.Y) {
                this.setStyles(barElement, {
                    height: barLengthStr,
                    top: barOffsetStr
                });
            } else {
                this.setStyles(barElement, {
                    width: barLengthStr,
                    left: barOffsetStr
                });
            }

            this.onScrollOffset.emit({
                axis,
                scrollOffset,
                barLengthStr
            });

            this.updateScrollEndingsVisibility(axis, {scrollLength, scrollOffset});
            this.setBarVisibility(axis, true);
        }
    }

    private setBarVisibility(axis: ScrollingAxis, isVisible: boolean): void {
        this.getScrollingBarByAxis(axis).setBarVisibility(isVisible);
    }

    private getScrollRatio(axis: ScrollingAxis): number {
        const containerNativeElement = this.containerElement.nativeElement;
        const totalLen = axis === ScrollingAxis.Y
            ? containerNativeElement.scrollHeight
            : containerNativeElement.scrollWidth;
        const ownLen = axis === ScrollingAxis.Y
            ? containerNativeElement.clientHeight
            : containerNativeElement.clientWidth;

        return ownLen / totalLen;
    }

    private listenToDragStart(): void {
        if (!this.isInactive) {
            const dragStartStreams = [ScrollingAxis.X, ScrollingAxis.Y].map(axis => {
                const bar = this.getScrollingBarByAxis(axis);

                return bar.getDragStartEvent().pipe(
                    tap(pos => this.startDrag(bar, pos))
                );
            });

            merge(...dragStartStreams).pipe(
                takeUntil(this.destroy$)
            ).subscribe();
        }
    }

    private startDrag(scrollingBar: ScrollingBarComponent, pointerClientPos: Point): void {
        const axis = scrollingBar.axis;
        const containerElement = this.containerElement.nativeElement;
        const startScrollPos = axis === ScrollingAxis.Y
            ? containerElement.scrollTop
            : containerElement.scrollLeft;
        this.movingInfo = {
            startPointerClientPos: pointerClientPos,
            pos:                   pointerClientPos,
            listeners:             [], // To be populated below.
            ratio:                 this.getScrollRatio(axis),
            axis,
            scrollingBar,
            startScrollPos
        };

        this.renderer.addClass(this.wrapperElement.nativeElement, DRAG_CLASS);
        scrollingBar.setDraggingState(true);

        this.listenToMove();
        this.listenToDrop();

        // send onScrollEvent
        this.onScrollEvent.emit({
            endOfMove: false,
            isUserScrolling: true
        });
    }

    private onMouseMove(event: MouseEvent): void {
        this.moveTo([event.clientX, event.clientY]);
    }

    private onTouchMove(event: TouchEvent): void {
        event.stopPropagation();

        const touch: Touch = event.changedTouches[0];
        this.moveTo([touch.clientX, touch.clientY]);
    }

    private moveTo(pointerClientPos: Point): void {
        const {axis, startPointerClientPos, startScrollPos, ratio} = this.movingInfo;
        const delta = pointerClientPos[axis] - startPointerClientPos[axis];

        this.setScrollingPosOnAxis(axis, startScrollPos + delta / ratio);
        this.movingInfo.pos = pointerClientPos;
    }

    private endOfMove(): void {
        if (this.movingInfo) {
            this.renderer.removeClass(this.wrapperElement.nativeElement, DRAG_CLASS);
            this.movingInfo.scrollingBar.setDraggingState(false);

            this.stopMovingListeners();

            this.movingInfo = null;
        }

        this.onScrollEvent.emit({
            endOfMove: true,
            isUserScrolling: true
        });
    }

    private listenToDrop(): void {
        const endOfMove = this.endOfMove.bind(this);

        this.movingInfo.listeners.push(
            this.renderer.listen(DOCUMENT_SELECTOR, 'mouseup', endOfMove),
            this.renderer.listen(DOCUMENT_SELECTOR, 'touchend', endOfMove),
            this.renderer.listen(DOCUMENT_SELECTOR, 'mouseleave', endOfMove)
        );
    }

    private listenToMove(): void {
        this.movingInfo.listeners.push(
            this.renderer.listen(DOCUMENT_SELECTOR, 'mousemove', this.onMouseMove.bind(this)),
            this.renderer.listen(DOCUMENT_SELECTOR, 'touchmove', this.onTouchMove.bind(this))
        );
    }

    private stopMovingListeners(): void {
        if (this.movingInfo) {
            this.movingInfo.listeners.forEach(unsubscribe => unsubscribe());
        }
    }

    private setStyles(element: HTMLElement, stylesMap: Partial<CSSStyleDeclaration>): void {
        const renderer = this.renderer;

        _.forEach(stylesMap, (value, key) =>
            renderer.setStyle(element, key, value)
        );
    }

    private disable(): void {
        this._isEnabled = false;

        const containerNativeElement: HTMLElement = this.containerElement.nativeElement;

        this.renderer.addClass(this.wrapperElement.nativeElement, DISABLED_SCROLL_CLASS);

        this.getScrollingBarByAxis(ScrollingAxis.X).setEnabledState(false);
        this.getScrollingBarByAxis(ScrollingAxis.Y).setEnabledState(false);

        containerNativeElement.scrollTop = 0;
        containerNativeElement.scrollLeft = 0;

        this.setStyles(containerNativeElement, {
            overflow: 'hidden',
            right: '0',
            bottom: '0'
        });
    }

    private enable(): void {
        this.renderer.removeStyle(this.containerElement.nativeElement, 'overflow');
        this.renderer.removeClass(this.wrapperElement.nativeElement, DISABLED_SCROLL_CLASS);

        this.getScrollingBarByAxis(ScrollingAxis.X).setEnabledState(true);
        this.getScrollingBarByAxis(ScrollingAxis.Y).setEnabledState(true);

        if (!this.isInactive) {
            this.updateContainer();
        }
    }

    private setEnabledState(isEnabled: boolean, forceChange?: boolean): void {
        isEnabled = isEnabled !== false;

        const isStateChanged = forceChange || !!isEnabled !== this._isEnabled;

        if (isStateChanged && this.isViewInitialized) {
            if (isEnabled) {
                this.enable();
            } else {
                this.disable();
            }
        }

        this._isEnabled = !!isEnabled;
    }

    private onScroll(): void {
        this.updateBars();
        this.scrollingStyle$.next();
        this.onScrollEvent.emit({
            endOfMove: !this.movingInfo,
            isUserScrolling: this.isUserNativeScrolling || !!this.movingInfo
        });
    }

    private listenToScrollingStyle(): void {
        let currentState: boolean = false;

        const setScrollingState = (isScrolling: boolean) => {
            if (currentState !== isScrolling) {
                currentState = isScrolling;

                this.toggleClass(this.wrapperElement.nativeElement, SCROLLING_CLASS, isScrolling);

                [ScrollingAxis.X, ScrollingAxis.Y].forEach(axis =>
                    this.getScrollingBarByAxis(axis).setScrollingState(isScrolling)
                );
            }
        };

        this.scrollingStyle$.pipe(
            tap(() => setScrollingState(true)),
            debounceTime(400),
            tap(() => setScrollingState(false)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private listenToNativeScrolling(): void {
        const USER_NATIVE_SCROLLING_TIMEOUT = 200;

        const containerElement = this.containerElement.nativeElement;

        const nativeScroll$ = merge(
            fromEvent(containerElement, 'wheel'),
            fromEvent(containerElement, 'mousewheel'),
            fromEvent(containerElement, 'DOMMouseScroll')
        );

        nativeScroll$.pipe(
            tap(() => this.isUserNativeScrolling = true),
            debounceTime(USER_NATIVE_SCROLLING_TIMEOUT),
            tap(() => this.isUserNativeScrolling = false),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private toggleClass(element: HTMLElement, className: string, status: boolean): void {
        if (status) {
            this.renderer.addClass(element, className);
        } else {
            this.renderer.removeClass(element, className);
        }
    }

}
